import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InputDate, InputTextCustom, Combobox, RadioButtonGroup } from "medere-ui-framework";
import { Dropdown } from "primereact/dropdown";
import "./PrescriptionStepStudyOrder.scss";
import { getAllFavoriteStudies, getFullFavoriteStudyById, getStudyTypes } from "../../../../services/FavoriteStudiesController";
import CustomCheckBox from "../../../../components/CustomCheckBox";
// import { getNomenclators } from "../../../../services/FavoriteStudiesController";
// import { NomenclatorDTO } from "../../../../dto/NomenclatorDTO";
import {
  FavoriteStudyDTO,
  NomenclatorCodeDTO,
  PatientBasic,
  StudyOrderData,
  StudyOrderDiseaseDTO,
  StudyOrderDiagnosisType,
  StudyOrderItem,
  StudyOrderMode,
  StudyTypeDTO,
} from "../../../../interface/interface";
import {
  getTerminologyApiErrorInfo,
  getTerminologyApiErrorMessage,
  getTerminologyCie10FromSnomed,
  getTerminologySnomedFromCie10,
  searchTerminologySnomedPage,
  TerminologySnomedOption,
} from "../../../../services/TerminologyController";
import { getNomenclatorCodes } from "../../../../services/NomenclatorController";
import { StudyOrderValidationRulesDTO } from "../../../../services/StudyOrderController";
import {
  getStudyOrderDiseaseByExactCie10,
  searchActiviaStudyOrderDiseases,
  searchStudyOrderDiseases,
} from "../../../../services/StudyOrderController";
import {
  buildItemValidationReasonMap,
  getFirstInvalidItemMessage,
  resolveItemSourceId,
} from "../studyOrderValidation";

const SOURCE_LABEL: Record<number, "NBU" | "SNOMED"> = { 1: "NBU", 2: "SNOMED" };

const DIAGNOSIS_TYPE_LABEL: Record<StudyOrderDiagnosisType, string> = {
  NBU: "CIE10",
  SNOMED: "SNOMED",
  FREE_TEXT: "Texto libre",
};

interface Props {
  selectedPatient: PatientBasic;
  healthPatientCoverageId?: number | null;
  studyOrderData: StudyOrderData;
  setStudyOrderData: React.Dispatch<React.SetStateAction<StudyOrderData>>;
  onValidationChange: (valid: boolean) => void;
  onError?: (error: any, message: string) => void;
  isActiviaFlow?: boolean;
  validationRules?: StudyOrderValidationRulesDTO | null;
}

type DiagnosisOptionValue = {
  code?: string;
  description: string;
  icd10Code?: string;
  icd10Display?: string;
};

type DiagnosisSelectOption = {
  label: string;
  value: DiagnosisOptionValue;
};

type SnomedDiagnosisPagingState = {
  query: string;
  items: TerminologySnomedOption[];
  page: number;
  hasNext: boolean;
  loading: boolean;
};

const SNOMED_SEARCH_BACKEND_PAGE_SIZE = 100;
const SNOMED_SEARCH_PREFETCH_PAGES_ON_NEW_QUERY = 8;

const ensureItemIds = (items: StudyOrderItem[] | undefined | null): StudyOrderItem[] => {
  const arr = Array.isArray(items) ? items : [];
  return arr.map((it, idx) => ({
    ...it,
    id: (it as any).id ?? `IT-${it.nomenclatorId ?? "X"}-${it.code ?? "NC"}-${idx}`,
  })) as any;
};

const normalize = (s = "") =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const deriveSourceIdFromStudyType = (st: StudyTypeDTO | null): 1 | 2 | null => {
  if (!st) return null;
  const desc = st.i18ncodes?.[0]?.description ?? "";
  return normalize(desc).includes("laboratorio") ? 1 : 2;
};

const favoriteMatchesStudyType = (favorite: FavoriteStudyDTO, selectedStudyType: StudyTypeDTO | null) => {
  if (!selectedStudyType) return true;
  const favoriteType = normalize((favorite as any)?.studytype ?? "");
  const selectedType = normalize(selectedStudyType?.i18ncodes?.[0]?.description ?? "");
  if (!favoriteType || !selectedType) return true;
  return favoriteType === selectedType;
};

const splitComposedByItems = (raw: string): string[] => {
  if (!raw?.trim()) return [];
  return raw
    .split(/,\s+(?=[A-Za-z0-9][^,]*-\s)/g)
    .map((s) => s.trim())
    .filter(Boolean);
};

const normalizeDiagnosisType = (
  value: StudyOrderDiagnosisType | null | undefined
): StudyOrderDiagnosisType =>
  value === "SNOMED" ? "SNOMED" : value === "FREE_TEXT" ? "FREE_TEXT" : value === "NBU" ? "NBU" : "NBU";

const SNOMED_BASE_URL_MISSING_MESSAGE = "No hay terminology.gateway.baseUrl configurada";

const buildDiagnosisSnomedSearchErrorMessage = (
  error: unknown,
  query: string
): string => {
  const fallback = `No se pudieron obtener resultados para "${query}".`;
  const details = getTerminologyApiErrorInfo(error);
  const backendMessage = getTerminologyApiErrorMessage(error, "").trim();
  const normalizedBackendMessage = backendMessage.toLowerCase();

  if (backendMessage.includes(SNOMED_BASE_URL_MISSING_MESSAGE)) {
    return "No se puede consultar SNOMED: falta configurar terminology.gateway.baseUrl en el backend.";
  }

  if (details.status === 401 || details.status === 403) {
    return "No autorizado para consultar SNOMED. Verifique el token del Terminology Gateway.";
  }

  if (details.status === 400) {
    return (
      backendMessage ||
      "No se pudo procesar la busqueda de diagnostico SNOMED. Revise los parametros enviados."
    );
  }

  if (details.status === 502 || details.status === 503 || details.status === 504) {
    return "Terminology Gateway no responde en este momento. Intente nuevamente.";
  }

  if (
    details.code === "ERR_NETWORK" ||
    details.code === "ECONNABORTED" ||
    normalizedBackendMessage.includes("no se pudo conectar")
  ) {
    return "No se pudo conectar con Terminology Gateway para buscar diagnosticos SNOMED.";
  }

  if (backendMessage && backendMessage !== "Unknown error") {
    return backendMessage;
  }

  return fallback;
};

export default function PrescriptionStepStudyOrder({
  selectedPatient,
  healthPatientCoverageId = null,
  studyOrderData,
  setStudyOrderData,
  onValidationChange,
  onError,
  isActiviaFlow = false,
  validationRules = null,
}: Props) {
  const hydratedFromCopyRef = useRef(false);
  const previousDiagnosisTypeRef = useRef<StudyOrderDiagnosisType | null>(null);

  const [studyTypes, setStudyTypes] = useState<StudyTypeDTO[]>([]);
  const [favourites, setFavourites] = useState<FavoriteStudyDTO[]>([]);

  // Búsqueda de nomenclador general deshabilitada temporalmente.
  // Se conserva comentado por si vuelve a habilitarse.
  // const [nomenclators, setNomenclators] = useState<NomenclatorDTO[]>([]);

  const [emissionDate, setEmissionDate] = useState<Date | null>(() => {
    const d: any = (studyOrderData as any).emissionDate;
    if (d instanceof Date) return d;
    if (typeof d === "string") return new Date(d);
    return new Date();
  });

  const [studyType, setStudyType] = useState<StudyTypeDTO | null>(
    (studyOrderData as any).studyType ?? null
  );

  const [diagnosisType, setDiagnosisType] = useState<StudyOrderDiagnosisType>(
    (studyOrderData as any).diagnosisType
      ? normalizeDiagnosisType((studyOrderData as any).diagnosisType)
      : "FREE_TEXT"
  );

  const [diagnosis, setDiagnosis] = useState((studyOrderData as any).diagnosis ?? "");
  const [diagnosisSnomedCode, setDiagnosisSnomedCode] = useState<string | null>(
    (studyOrderData as any).diagnosisSnomedCode ?? null
  );
  const [diagnosisSnomedDisplay, setDiagnosisSnomedDisplay] = useState<string | null>(
    (studyOrderData as any).diagnosisSnomedDisplay ?? null
  );
  const [diagnosisIcd10Code, setDiagnosisIcd10Code] = useState<string | null>(
    (studyOrderData as any).diagnosisIcd10Code ?? null
  );
  const [diagnosisIcd10Display, setDiagnosisIcd10Display] = useState<string | null>(
    (studyOrderData as any).diagnosisIcd10Display ?? null
  );
  const [sampleDescription, setSampleDescription] = useState(
    (studyOrderData as any).sampleDescription ?? ""
  );
  const [hidePatientData, setHidePatientData] = useState(
    (studyOrderData as any).hidePatientData ?? false
  );
  const diagnosisSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDiagnosisResolveRef = useRef<((value: DiagnosisSelectOption[]) => void) | null>(null);
  const snomedDiagnosisPagingRef = useRef<SnomedDiagnosisPagingState>({
    query: "",
    items: [],
    page: -1,
    hasNext: true,
    loading: false,
  });

  const [items, setItems] = useState<StudyOrderItem[]>(
    ensureItemIds((studyOrderData as any).items)
  );

  const [mode, setMode] = useState<StudyOrderMode>("FREE_TEXT");
  const [selectedFavourite, setSelectedFavourite] = useState<any>(null);
  const [freeText, setFreeText] = useState("");

  const [selectedDiagnosisOption, setSelectedDiagnosisOption] =
    useState<DiagnosisSelectOption | null>(null);
  const [diagnosisSearchMessage, setDiagnosisSearchMessage] = useState("");

  // Texto libre deshabilitado temporalmente para estudios a realizar.
  // const [freeText, setFreeText] = useState("");

  const [selectedSourceCode, setSelectedSourceCode] = useState<{
    label: string;
    value: NomenclatorCodeDTO;
  } | null>(null);
  const [sourceSelectionMessage, setSourceSelectionMessage] = useState("");

  useEffect(() => {
    getStudyTypes()
      .then((data: any) => {
        const resolved = Array.isArray(data) ? data
          : Array.isArray(data?.data) ? data.data
          : Array.isArray(data?.content) ? data.content
          : [];
        setStudyTypes(resolved);
      })
      .catch(() => setStudyTypes([]));

    getAllFavoriteStudies(0, 50)
      .then((data: FavoriteStudyDTO[]) => setFavourites(data ?? []))
      .catch(() => setFavourites([]));

    // Búsqueda de nomenclador general deshabilitada temporalmente.
    // getNomenclators("")
    //   .then((data: NomenclatorDTO[]) => setNomenclators(data ?? []))
    //   .catch(() => setNomenclators([]));
  }, []);

  const handleDiagnosisSearchError = useCallback(
    (error: any, normalizedFilter: string) => {
      const errorMessage =
        normalizeDiagnosisType(diagnosisType) === "SNOMED"
          ? buildDiagnosisSnomedSearchErrorMessage(error, normalizedFilter)
          : `No se pudieron obtener resultados para "${normalizedFilter}".`;
      setDiagnosisSearchMessage(errorMessage);
      onError?.(error, errorMessage);
    },
    [diagnosisType, onError]
  );

  const resetSnomedDiagnosisPaging = useCallback(() => {
    snomedDiagnosisPagingRef.current = {
      query: "",
      items: [],
      page: -1,
      hasNext: true,
      loading: false,
    };
  }, []);

  const mergeUniqueByCode = useCallback(
    (base: TerminologySnomedOption[], incoming: TerminologySnomedOption[]): TerminologySnomedOption[] => {
      const byCode = new Map<string, TerminologySnomedOption>();
      [...base, ...incoming].forEach((item) => {
        const key = (item.code ?? "").trim().toLowerCase();
        if (!key) return;
        if (!byCode.has(key)) {
          byCode.set(key, item);
        }
      });
      return Array.from(byCode.values());
    },
    []
  );

  const mapDiagnosisItemsToOptions = useCallback(
    (itemsToMap: TerminologySnomedOption[]): DiagnosisSelectOption[] =>
      itemsToMap.map((item) => ({
        label: `${item.code} - ${item.description}`,
        value: {
          code: item.code,
          description: item.description,
          icd10Code: item.cie10code,
          icd10Display: item.cie10Description ?? item.cie10title,
        },
      })),
    []
  );

  const normalizeSearchText = useCallback(
    (value?: string | null): string =>
      normalize(value ?? "")
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim(),
    []
  );

  const scoreSnomedDiagnosisRelevance = useCallback(
    (item: TerminologySnomedOption, rawQuery: string): number => {
      const query = normalizeSearchText(rawQuery);
      if (!query) return 0;

      const queryTokens = query.split(" ").filter(Boolean);
      const description = normalizeSearchText(item.description);
      const descriptionTokens = description.split(" ").filter(Boolean);
      const code = normalizeSearchText(item.code);
      let score = 0;

      if (code === query) score += 1000;
      if (description === query) score += 900;
      if (description.startsWith(query)) score += 850;
      if (description.includes(query)) score += 700;
      if ((" " + description + " ").includes(" " + query + " ")) score += 650;

      const allTokensIncluded =
        queryTokens.length > 0 && queryTokens.every((token) => description.includes(token));
      if (allTokensIncluded) score += 500;

      queryTokens.forEach((token) => {
        if (descriptionTokens.some((word) => word.startsWith(token))) score += 150;
        else if (description.includes(token)) score += 80;

        if ((" " + description + " ").includes(" " + token + " ")) score += 120;
        if (code.startsWith(token)) score += 60;
      });

      return score;
    },
    [normalizeSearchText]
  );

  const sortDiagnosisItemsByRelevance = useCallback(
    (itemsToSort: TerminologySnomedOption[], query: string): TerminologySnomedOption[] =>
      [...itemsToSort]
        .map((item, index) => ({
          item,
          index,
          score: scoreSnomedDiagnosisRelevance(item, query),
        }))
        .sort((a, b) => b.score - a.score || a.index - b.index)
        .map((entry) => entry.item),
    [scoreSnomedDiagnosisRelevance]
  );

  const shouldFetchMoreSnomedPages = useCallback(
    (
      rankedItems: TerminologySnomedOption[],
      query: string,
      requestedOffset: number,
      requestedSize: number
    ): boolean => {
      const queryScoreThreshold = 1100;
      const rankedStrongMatches = rankedItems.filter(
        (item) => scoreSnomedDiagnosisRelevance(item, query) >= queryScoreThreshold
      ).length;

      if (rankedStrongMatches >= Math.max(3, Math.floor(requestedSize / 2))) {
        return false;
      }

      const pageEnd = requestedOffset + requestedSize;
      return rankedItems.length < pageEnd + requestedSize;
    },
    [scoreSnomedDiagnosisRelevance]
  );

  const resolveIcd10Display = useCallback(async (code: string, currentDisplay?: string | null) => {
    const normalizedCode = (code ?? "").trim();
    const normalizedDisplay = (currentDisplay ?? "").trim();
    if (!normalizedCode) {
      return null;
    }
    if (normalizedDisplay) {
      return normalizedDisplay;
    }
    try {
      const disease = await getStudyOrderDiseaseByExactCie10(normalizedCode);
      return (disease?.name ?? "").trim() || null;
    } catch {
      return null;
    }
  }, []);

  // useEffect(() => {
  //   if (mode === "NOMENCLATOR") {
  //     getNomenclators("")
  //       .then((data: NomenclatorDTO[]) => setNomenclators(data ?? []))
  //       .catch(() => setNomenclators([]));
  //     setSelectedNomenclator(null);
  //   }
  // }, [mode]);

  useEffect(() => {
    if (hydratedFromCopyRef.current) return;

    const incomingItems = ensureItemIds((studyOrderData as any).items);
    const incomingHasCopyData =
      incomingItems.length > 0 ||
      !!(studyOrderData as any).studyTypeId ||
      !!(studyOrderData as any).diagnosis ||
      !!(studyOrderData as any).studyType;

    if (!incomingHasCopyData) return;

    const d: any = (studyOrderData as any).emissionDate;
    const incomingDate =
      d instanceof Date ? d : typeof d === "string" ? new Date(d) : new Date();

    setEmissionDate(incomingDate);

    const incomingDiagnosisType = normalizeDiagnosisType(
      (studyOrderData as any).diagnosisType as StudyOrderDiagnosisType | null
    );
    const incomingDiagnosis = (studyOrderData as any).diagnosis ?? "";
    const incomingDiagnosisSnomedCode = (studyOrderData as any).diagnosisSnomedCode ?? null;
    const incomingDiagnosisSnomedDisplay =
      (studyOrderData as any).diagnosisSnomedDisplay ?? null;
    const incomingDiagnosisIcd10Code = (studyOrderData as any).diagnosisIcd10Code ?? null;
    const incomingDiagnosisIcd10Display =
      (studyOrderData as any).diagnosisIcd10Display ?? null;

    setDiagnosisType(incomingDiagnosisType);
    setDiagnosis(incomingDiagnosis);
    setDiagnosisSnomedCode(incomingDiagnosisSnomedCode);
    setDiagnosisSnomedDisplay(incomingDiagnosisSnomedDisplay);
    setDiagnosisIcd10Code(incomingDiagnosisIcd10Code);
    setDiagnosisIcd10Display(incomingDiagnosisIcd10Display);

    setSelectedDiagnosisOption(
      !incomingDiagnosis
        ? null
        : {
            label:
              incomingDiagnosisType === "SNOMED"
                ? [incomingDiagnosisSnomedCode, incomingDiagnosisSnomedDisplay ?? incomingDiagnosis]
                    .filter(Boolean)
                    .join(" - ")
                : [incomingDiagnosisIcd10Code, incomingDiagnosisIcd10Display ?? incomingDiagnosis]
                    .filter(Boolean)
                    .join(" - "),
            value: {
              code:
                incomingDiagnosisType === "SNOMED"
                  ? incomingDiagnosisSnomedCode ?? undefined
                  : incomingDiagnosisIcd10Code ?? undefined,
              description:
                incomingDiagnosisType === "SNOMED"
                  ? incomingDiagnosisSnomedDisplay ?? incomingDiagnosis
                  : incomingDiagnosisIcd10Display ?? incomingDiagnosis,
              icd10Code: incomingDiagnosisIcd10Code ?? undefined,
              icd10Display: incomingDiagnosisIcd10Display ?? undefined,
            },
          }
    );

    setSampleDescription((studyOrderData as any).sampleDescription ?? "");
    setHidePatientData((studyOrderData as any).hidePatientData ?? false);
    setItems(incomingItems);

    if ((studyOrderData as any).studyType) {
      setStudyType((studyOrderData as any).studyType);
    }

    hydratedFromCopyRef.current = true;
  }, [studyOrderData]);

  useEffect(() => {
    const id = (studyOrderData as any).studyTypeId;
    if (!id) return;
    if (studyType) return;
    if (!studyTypes.length) return;

    const found =
      studyTypes.find((t) => (t as any).studyType === id) ??
      studyTypes.find((t) => Number((t as any).studyType) === Number(id)) ??
      null;

    if (found) {
      setStudyType(found);
      setStudyOrderData((prev) => ({ ...prev, studyType: found } as any));
    }
  }, [studyTypes, studyOrderData, studyType, setStudyOrderData]);

  useEffect(() => {
    setStudyOrderData((prev) =>
      ({
        ...prev,
        emissionDate,
        studyType,
        studyTypeId: (studyType as any)?.studyType ?? (prev as any).studyTypeId ?? null,
        diagnosisType,
        diagnosis,
        diagnosisSnomedCode,
        diagnosisSnomedDisplay,
        diagnosisIcd10Code,
        diagnosisIcd10Display,
        sampleDescription,
        hidePatientData,
        items,
      } as any)
    );
  }, [
    emissionDate,
    studyType,
    diagnosisType,
    diagnosis,
    diagnosisSnomedCode,
    diagnosisSnomedDisplay,
    diagnosisIcd10Code,
    diagnosisIcd10Display,
    sampleDescription,
    hidePatientData,
    items,
    setStudyOrderData,
  ]);

  const MAX_DIAGNOSIS_LEN = 255;
  const diagnosisTooLong = (diagnosis ?? "").length > MAX_DIAGNOSIS_LEN;
  const allowedSourceIds = useMemo(
    () =>
      Array.from(
        new Set(
          (validationRules?.allowedNomenclatorSourceIds ?? [])
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      ),
    [validationRules]
  );
  const requiresMappedCie10 = Boolean(validationRules?.diagnosisMappedToCie10Required);
  const diagnosisMappingMissing =
    requiresMappedCie10 &&
    normalizeDiagnosisType(diagnosisType) !== "FREE_TEXT" &&
    normalizeDiagnosisType(diagnosisType) === "SNOMED" &&
    !!diagnosis.trim() &&
    !diagnosisIcd10Code;

  useEffect(() => {
    const invalidReason = getFirstInvalidItemMessage({
      items,
      diagnosisType,
      diagnosis,
      diagnosisIcd10Code,
      requiresMappedCie10,
      allowedSourceIds,
    });

    const valid =
      !!emissionDate &&
      !!studyType &&
      items.length > 0 &&
      !!diagnosis.trim() &&
      !diagnosisTooLong &&
      !diagnosisMappingMissing &&
      !invalidReason;

    onValidationChange(valid);
  }, [
    emissionDate,
    studyType,
    items,
    diagnosis,
    diagnosisTooLong,
    diagnosisMappingMissing,
    diagnosisType,
    diagnosisIcd10Code,
    requiresMappedCie10,
    allowedSourceIds,
    onValidationChange,
  ]);

  useEffect(() => {
    if (previousDiagnosisTypeRef.current === null) {
      previousDiagnosisTypeRef.current = diagnosisType;
      return;
    }

    if (previousDiagnosisTypeRef.current === diagnosisType) return;

    previousDiagnosisTypeRef.current = diagnosisType;

    setSelectedDiagnosisOption(null);
    setDiagnosis("");
    setDiagnosisSnomedCode(null);
    setDiagnosisSnomedDisplay(null);
    setDiagnosisIcd10Code(null);
    setDiagnosisIcd10Display(null);
    setDiagnosisSearchMessage("");
    resetSnomedDiagnosisPaging();
  }, [diagnosisType, resetSnomedDiagnosisPaging]);

  const studyTypeDropdownOptions = useMemo(
    () => {
      const list = Array.isArray(studyTypes) ? studyTypes : [];
      return list.map((t: any) => ({
        label: t.i18ncodes?.[0]?.description ?? `Tipo ${t.studyType}`,
        value: t.studyType,
      }));
    },
    [studyTypes],
  );

  const sourceId = useMemo(() => {
    if (allowedSourceIds.length === 1) {
      const forced = allowedSourceIds[0];
      if (forced === 1 || forced === 2) {
        return forced as 1 | 2;
      }
    }
    if (isActiviaFlow) return 1 as 1;

    const derived = deriveSourceIdFromStudyType(studyType);
    if (!derived) {
      return null;
    }
    if (allowedSourceIds.length > 0 && !allowedSourceIds.includes(derived)) {
      return null;
    }
    return derived;
  }, [studyType, isActiviaFlow, allowedSourceIds]);
  const sourceLabel = sourceId ? SOURCE_LABEL[sourceId] : null;
  const studyTypeKey =
    Number((studyType as any)?.studyType ?? (studyOrderData as any)?.studyTypeId ?? 0) || 0;

  const invalidActiviaItems = useMemo(
    () =>
      allowedSourceIds.length === 0
        ? []
        : items.filter(
            (it: any) => {
              const source = resolveItemSourceId(it);
              return source === null || !allowedSourceIds.includes(source);
            }
          ),
    [allowedSourceIds, items]
  );
  const invalidItemReasonById = useMemo(() => {
    return buildItemValidationReasonMap(items, allowedSourceIds);
  }, [allowedSourceIds, items]);

  useEffect(() => {
    setSelectedSourceCode(null);
    setSourceSelectionMessage("");
  }, [studyTypeKey, sourceId]);

  useEffect(() => {
    setSelectedFavourite(null);
  }, [studyTypeKey]);

  useEffect(() => {
    if (!items.length || allowedSourceIds.length === 0) return;

    const invalidItems = items.filter((it: any) => {
      if (it?.origin === "FREE_TEXT") return false;
      const source = resolveItemSourceId(it);
      return source === null || !allowedSourceIds.includes(source);
    });

    if (!invalidItems.length) return;

    const invalidIds = new Set(invalidItems.map((it: any) => String(it.id)));
    setItems((prev) => prev.filter((it: any) => !invalidIds.has(String(it.id))));

    const firstReason = getFirstInvalidItemMessage({
      items: invalidItems,
      diagnosisType,
      diagnosis,
      diagnosisIcd10Code,
      requiresMappedCie10,
      allowedSourceIds,
    });
    const message =
      firstReason ||
      "Se removieron practicas incompatibles con el tipo de diagnostico o nomenclador seleccionado.";
    setSourceSelectionMessage(message);
    onError?.(new Error(message), message);
  }, [allowedSourceIds, diagnosis, diagnosisIcd10Code, diagnosisType, items, onError, requiresMappedCie10]);

  useEffect(() => {
    return () => {
      if (diagnosisSearchTimeoutRef.current) {
        clearTimeout(diagnosisSearchTimeoutRef.current);
      }

      if (pendingDiagnosisResolveRef.current) {
        pendingDiagnosisResolveRef.current([]);
        pendingDiagnosisResolveRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!sourceId) return;
    setMode("NOMENCLATOR_SOURCE");
  }, [sourceId]);

  const loadDiagnosisOptions = useCallback(
    ({
      filterValue,
      offSet,
      pageSize,
    }: {
      filterValue?: string;
      offSet?: number;
      pageSize?: number;
    }) => {
      const normalizedFilter = (filterValue ?? "").trim();

      const isSnomed = normalizeDiagnosisType(diagnosisType) === "SNOMED";
      const minLength = isSnomed ? 3 : 1;

      if (!normalizedFilter || normalizedFilter.length < minLength) {
        setDiagnosisSearchMessage("");
        resetSnomedDiagnosisPaging();

        if (diagnosisSearchTimeoutRef.current) {
          clearTimeout(diagnosisSearchTimeoutRef.current);
          diagnosisSearchTimeoutRef.current = null;
        }

        if (pendingDiagnosisResolveRef.current) {
          pendingDiagnosisResolveRef.current([]);
          pendingDiagnosisResolveRef.current = null;
        }

        return Promise.resolve([]);
      }

      if (diagnosisSearchTimeoutRef.current) {
        clearTimeout(diagnosisSearchTimeoutRef.current);
      }

      if (pendingDiagnosisResolveRef.current) {
        pendingDiagnosisResolveRef.current([]);
        pendingDiagnosisResolveRef.current = null;
      }

      return new Promise<DiagnosisSelectOption[]>((resolve) => {
        pendingDiagnosisResolveRef.current = resolve;

        diagnosisSearchTimeoutRef.current = setTimeout(async () => {
          let hasShownDiagnosisError = false;
          try {
            if (normalizeDiagnosisType(diagnosisType) === "SNOMED") {
              const requestedSize = pageSize ?? 20;
              const requestedOffset = Math.max(offSet ?? 0, 0);
              const state = snomedDiagnosisPagingRef.current;
              const queryChanged = state.query !== normalizedFilter;

              if (queryChanged) {
                resetSnomedDiagnosisPaging();
              }

              const pagingState = snomedDiagnosisPagingRef.current;
              let items = pagingState.items;

              const shouldFetch =
                !pagingState.loading &&
                (
                  queryChanged ||
                  (pagingState.hasNext && (requestedOffset + requestedSize > pagingState.items.length))
                );

              if (shouldFetch) {
                pagingState.loading = true;
                try {
                  let workingItems = queryChanged ? [] : pagingState.items;
                  let currentPage = queryChanged ? -1 : pagingState.page;
                  let hasNext = queryChanged ? true : pagingState.hasNext;
                  const maxIterations = queryChanged
                    ? SNOMED_SEARCH_PREFETCH_PAGES_ON_NEW_QUERY
                    : 1;

                  for (let i = 0; i < maxIterations && hasNext; i += 1) {
                    const nextPage = currentPage + 1;
                    const pageResult = await searchTerminologySnomedPage(normalizedFilter, {
                      context: "DIAGNOSIS",
                      language: "es",
                      page: Math.max(nextPage, 0),
                      size: SNOMED_SEARCH_BACKEND_PAGE_SIZE,
                      mappedToCie10Only: false,
                    });

                    workingItems = mergeUniqueByCode(workingItems, pageResult.items);
                    currentPage = pageResult.page;
                    hasNext = pageResult.hasNext;

                    const rankedSoFar = sortDiagnosisItemsByRelevance(workingItems, normalizedFilter);
                    const needsMore = shouldFetchMoreSnomedPages(
                      rankedSoFar,
                      normalizedFilter,
                      requestedOffset,
                      requestedSize
                    );

                    if (!needsMore) {
                      workingItems = rankedSoFar;
                      break;
                    }

                    workingItems = rankedSoFar;
                  }

                  const rankedItems = sortDiagnosisItemsByRelevance(workingItems, normalizedFilter);
                  snomedDiagnosisPagingRef.current = {
                    query: normalizedFilter,
                    items: rankedItems,
                    page: currentPage,
                    hasNext,
                    loading: false,
                  };
                  items = rankedItems;
                } catch (error) {
                  pagingState.loading = false;
                  if (!hasShownDiagnosisError) {
                    handleDiagnosisSearchError(error, normalizedFilter);
                    hasShownDiagnosisError = true;
                  }
                  throw error;
                }
              }

              // El Combobox maneja el append por pagina internamente:
              // devolvemos solo el tramo solicitado para evitar duplicados al scrollear.
              const pagedItems = items.slice(requestedOffset, requestedOffset + requestedSize);
              const options = mapDiagnosisItemsToOptions(pagedItems);
              const locallyFilteredOptions = !normalizedFilter
                ? options
                : options.filter((opt) => {
                    const code = normalize((opt?.value?.code ?? "").toString());
                    const description = normalize((opt?.value?.description ?? "").toString());
                    const label = normalize((opt?.label ?? "").toString());
                    const needle = normalize(normalizedFilter);
                    return code.includes(needle) || description.includes(needle) || label.includes(needle);
                  });

              setDiagnosisSearchMessage(
                locallyFilteredOptions.length === 0 && normalizedFilter
                  ? `No se encontraron resultados para "${normalizedFilter}".`
                  : ""
              );

              resolve(locallyFilteredOptions);
              pendingDiagnosisResolveRef.current = null;
              return;
            }

            const diseases = isActiviaFlow
              ? await searchActiviaStudyOrderDiseases({
                  filterValue: normalizedFilter,
                  patientId: (selectedPatient as any)?.medereentity ?? null,
                  healthPatientCoverageId,
                  offSet: offSet ?? 0,
                  pageSize: pageSize ?? 20,
                })
              : await searchStudyOrderDiseases(
                  normalizedFilter,
                  offSet ?? 0,
                  pageSize ?? 20
                );

            const options = diseases.map((item: StudyOrderDiseaseDTO) => ({
              label: `${item.cie10} - ${item.name}`,
              value: {
                code: item.cie10,
                description: item.name,
                icd10Code: item.cie10,
                icd10Display: item.name,
              },
            }));
            const locallyFilteredOptions = !normalizedFilter
              ? options
              : options.filter((opt) => {
                  const code = normalize((opt?.value?.code ?? "").toString());
                  const description = normalize((opt?.value?.description ?? "").toString());
                  const label = normalize((opt?.label ?? "").toString());
                  const needle = normalize(normalizedFilter);
                  return code.includes(needle) || description.includes(needle) || label.includes(needle);
                });

            setDiagnosisSearchMessage(
              locallyFilteredOptions.length === 0 && normalizedFilter
                ? `No se encontraron resultados para "${normalizedFilter}".`
                : ""
            );

            resolve(locallyFilteredOptions);
          } catch (error) {
            if (!hasShownDiagnosisError) {
              handleDiagnosisSearchError(error, normalizedFilter);
            }
            resolve([]);
          } finally {
            pendingDiagnosisResolveRef.current = null;
          }
        }, 400);
      });
    },
    [
      diagnosisType,
      healthPatientCoverageId,
      handleDiagnosisSearchError,
      isActiviaFlow,
      mapDiagnosisItemsToOptions,
      mergeUniqueByCode,
      sortDiagnosisItemsByRelevance,
      resetSnomedDiagnosisPaging,
      selectedPatient,
    ]
  );
  const addItems = (newOnes: StudyOrderItem[]) => {
    if (!newOnes.length) return;

    setItems((prev) => {
      const keyOf = (it: any) => {
        const codeKey = normalize((it.code ?? "").toString());
        const descKey = normalize((it.description ?? "").toString().replace(/\s+/g, " "));
        return `${codeKey}||${descKey}`;
      };

      const existingKeys = new Set(prev.map(keyOf));
      const filtered: StudyOrderItem[] = [];

      newOnes.forEach((it, idx) => {
        const candidate = {
          ...it,
          id: (it as any).id ?? `ADD-${Date.now()}-${idx}-${it.code ?? "NC"}`,
        } as StudyOrderItem;

        const key = keyOf(candidate);
        if (existingKeys.has(key)) return;

        existingKeys.add(key);
        filtered.push(candidate);
      });

      return [...prev, ...filtered];
    });
  };

  const handleAddFromFavourite = async () => {
    if (!selectedFavourite?.favouritestudy) return;
    if (!favoriteMatchesStudyType(selectedFavourite as FavoriteStudyDTO, studyType)) {
      const message = "El favorito seleccionado no corresponde al tipo de estudio actual.";
      onError?.(new Error(message), message);
      return;
    }

    const favouriteId = Number((selectedFavourite as any).favouritestudy ?? 0) || 0;
    let newItems: StudyOrderItem[] = [];

    if (favouriteId > 0) {
      try {
        const fullFavorite = await getFullFavoriteStudyById(favouriteId);
        const detailItems = Array.isArray((fullFavorite as any)?.items)
          ? (fullFavorite as any).items
          : [];

        newItems = detailItems
          .map((it: any, idx: number) => {
            const code = (it.code ?? it.nomenclatorcode ?? "").toString().trim();
            const description = (it.description ?? it.nomenclatordescription ?? "")
              .toString()
              .trim();
            const nomenclatorId = (it.nomenclatorid as number | null) ?? null;

            if (!code && !description) return null;

            return {
              id: `FAV-${favouriteId}-${idx}-${code || "NC"}`,
              code: code || undefined,
              description,
              nomenclatorId,
              nomenclatorSourceId: !nomenclatorId && code && sourceId ? sourceId : null,
              origin: !nomenclatorId && code && sourceId ? "NOMENCLATOR_SOURCE" : "FAVOURITE",
            } as StudyOrderItem;
          })
          .filter(Boolean) as StudyOrderItem[];
      } catch {
        // fallback to composedby parsing
      }
    }

    if (!newItems.length) {
      const raw = (selectedFavourite as any).composedby ?? "";
      const parts = splitComposedByItems(raw);
      if (!parts.length) return;

      newItems = parts.map((p: string, idx: number) => {
        const [codePart, ...descParts] = p.split("-");
        const code = (codePart ?? "").trim();
        const description = descParts.join("-").trim();

        return {
          id: `FAV-${favouriteId || "X"}-${idx}-${code || "NC"}`,
          code: code || undefined,
          description,
          nomenclatorSourceId: code && sourceId ? sourceId : null,
          origin: code && sourceId ? "NOMENCLATOR_SOURCE" : "FAVOURITE",
        } as StudyOrderItem;
      });
    }

    addItems(newItems);
    setSelectedFavourite(null);
  };

  // Búsqueda de nomenclador general deshabilitada temporalmente.
  // const handleAddFromNomenclator = () => {
  //   if (!selectedNomenclator) return;
  //
  //   const n = selectedNomenclator.value;
  //   const item: StudyOrderItem = {
  //     id: `NOM-${(n as any).nomenclator}`,
  //     code: (n as any).code ?? undefined,
  //     description: (n as any).description ?? "",
  //     nomenclatorId: (n as any).nomenclator ?? null,
  //     origin: "NOMENCLATOR",
  //   } as any;
  //
  //   addItems([item]);
  //   setSelectedNomenclator(null);
  // };

  const handleAddFreeText = () => {
    if (!freeText.trim()) return;
    const item: StudyOrderItem = {
      id: `FT-${Date.now()}-${items.length}`,
      description: freeText.trim(),
      origin: "FREE_TEXT",
    } as any;
    addItems([item]);
    setFreeText("");
  };

  const handleAddFromSourceNomenclator = () => {
    if (!selectedSourceCode || !sourceId) return;
    const n = selectedSourceCode.value;
    const supported = n.supportedByRepository !== false;
    const unsupportedReason = (n.unsupportedReason ?? "").toString().trim() || null;

    if (!supported) {
      const message =
        unsupportedReason ||
        "La practica seleccionada no se puede validar con la cobertura/repositorio actual.";
      setSourceSelectionMessage(message);
      onError?.(new Error(message), message);
      return;
    }

    const item: StudyOrderItem = {
      id: `SRC-${sourceId}-${n.code ?? n.id}`,
      code: n.code ?? undefined,
      description: n.description ?? "",
      nomenclatorId: null,
      nomenclatorSourceId: sourceId,
      origin: "NOMENCLATOR_SOURCE",
      supportedByRepository: supported,
      unsupportedReason: unsupportedReason,
    } as any;

    addItems([item]);
    setSelectedSourceCode(null);
    setSourceSelectionMessage("");
  };

  const handleRemoveItem = (id: string) => {
    setItems((prev) => prev.filter((it) => (it as any).id !== id));
  };

  const renderModeControls = () => {
    if (mode === "NOMENCLATOR_SOURCE") {
      return (
        <div className="ns-mode-block">
          <Combobox
            key={`src-${studyTypeKey}-${sourceId ?? "x"}`}
            className="border2"
            placeholder={
              !sourceId
                ? "Buscar por nomenclador (seleccione primero un tipo de estudio)"
                : sourceId === 2
                  ? `Escribir al menos 3 letras para buscar en ${sourceLabel ?? "nomenclador SNOMED"}`
                  : `Buscar por nomenclador en ${sourceLabel ?? "nomenclador"} por código o descripción`
            }
            optionLabel="label"
            pageSizeParam={20}
            value={selectedSourceCode}
            setValue={(option: any) => {
              setSelectedSourceCode(option ?? null);
              const unsupported = option?.value?.supportedByRepository === false;
              const reason = (option?.value?.unsupportedReason ?? "").toString().trim();
              if (unsupported) {
                setSourceSelectionMessage(
                  reason ||
                    "La practica seleccionada no se puede validar con la cobertura/repositorio actual."
                );
              } else {
                setSourceSelectionMessage("");
              }
            }}
            style={{ width: "100%" }}
            disable={!sourceId}
            params={
              sourceId
                ? {
                    nomenclatorsourceid: sourceId,
                    patientId: (selectedPatient as any)?.medereentity ?? undefined,
                    healthPatientCoverageId: healthPatientCoverageId ?? undefined,
                  }
                : {}
            }
            getItems={(props) => {
              if (!sourceId) return Promise.resolve([]);
              if (sourceId === 2 && (props.filterValue ?? "").trim().length < 3) return Promise.resolve([]);

              return getNomenclatorCodes(props).then((items) =>
                items.map((n) => {
                  const desc = (n.description ?? "").replace(/\s+/g, " ").trim();
                  return {
                    label:
                      n.supportedByRepository === false
                        ? `${n.code} - ${desc} (No soportado)`
                        : `${n.code} - ${desc}`,
                    value: n,
                  };
                })
              );
            }}
          />

          <button
            type="button"
            className="ns-add-button"
            onClick={handleAddFromSourceNomenclator}
            disabled={
              !sourceId ||
              !selectedSourceCode ||
              selectedSourceCode?.value?.supportedByRepository === false
            }
          >
            Agregar estudio
          </button>

          {!sourceId && (
            <small className="ns-helper-text">
              Seleccione primero un tipo de estudio para habilitar la búsqueda por nomenclador.
            </small>
          )}
{!!sourceSelectionMessage && <small className="ns-helper-text">{sourceSelectionMessage}</small>}
        </div>
      );
    }

    if (mode === "FREE_TEXT") {
      return (
        <div className="ns-mode-block">
          {validationRules?.repositoryConfigured && (
            <small className="ns-helper-text ns-warning-text">
              La obra social exige codificar por SNOMED. Puede ingresar texto libre, pero la orden podría requerir aprobación manual.
            </small>
          )}
          <InputTextCustom
            className="border2"
            value={freeText}
            onChange={(e: any) => setFreeText(e.target.value)}
            placeholder="Descripción del estudio"
            style={{ width: "100%" }}
          />
          <button
            type="button"
            className="ns-add-button"
            onClick={handleAddFreeText}
            disabled={!freeText.trim()}
          >
            Agregar estudio
          </button>
        </div>
      );
    }

    if (mode === "FAVOURITE") {
      return (
        <div className="ns-mode-block">
          <Combobox
            key={`fav-${studyTypeKey || "x"}`}
            className="border2"
            placeholder="Seleccionar lista de favoritos"
            optionLabel="favouritestudydesc"
            getItems={async ({ filterValue }) => {
              const f = normalize((filterValue ?? "").toString());
              return (favourites ?? []).filter((fav: any) => {
                if (!favoriteMatchesStudyType(fav as FavoriteStudyDTO, studyType)) return false;
                if (!f) return true;
                const byName = normalize((fav?.favouritestudydesc ?? "").toString());
                const byComposed = normalize((fav?.composedby ?? "").toString());
                return byName.includes(f) || byComposed.includes(f);
              });
            }}
            value={selectedFavourite}
            setValue={(opt: any) => setSelectedFavourite(opt ?? null)}
            style={{ width: "100%" }}
          />
          <button
            type="button"
            className="ns-add-button"
            onClick={handleAddFromFavourite}
            disabled={!selectedFavourite?.favouritestudy}
          >
            Agregar estudio
          </button>
        </div>
      );
    }

    return null;
  };

  const renderDiagnosisControl = () => {
    if (normalizeDiagnosisType(diagnosisType) === "FREE_TEXT") {
      return (
        <InputTextCustom
          className={`border2 ${diagnosisTooLong ? "ns-error-input" : ""}`}
          value={diagnosis}
          onChange={(e: any) => {
            setDiagnosis(e.target.value);
            setDiagnosisSnomedCode(null);
            setDiagnosisSnomedDisplay(null);
            setDiagnosisIcd10Code(null);
            setDiagnosisIcd10Display(null);
          }}
          placeholder="Diagnóstico presuntivo"
          style={{ width: "100%" }}
          error={diagnosisTooLong}
          caption={diagnosisTooLong ? "El tamaño máximo de un diagnóstico es 255 caracteres." : ""}
        />
      );
    }

    return (
      <Combobox
        key={`diag-combobox-${normalizeDiagnosisType(diagnosisType)}`}
        pageSizeParam={20}
        value={selectedDiagnosisOption}
        setValue={async (option: DiagnosisSelectOption | null) => {
        setDiagnosisSearchMessage("");
        if (!option) {
          setSelectedDiagnosisOption(null);
          setDiagnosis("");
          setDiagnosisSnomedCode(null);
          setDiagnosisSnomedDisplay(null);
          setDiagnosisIcd10Code(null);
          setDiagnosisIcd10Display(null);
          return;
        }

        setSelectedDiagnosisOption(option);

        const nextDiagnosis = option.value?.description ?? "";
        setDiagnosis(nextDiagnosis);

        if (normalizeDiagnosisType(diagnosisType) === "SNOMED") {
          const snomedCode = option.value.code ?? null;
          const snomedDisplay = option.value.description ?? null;
          setDiagnosisSnomedCode(snomedCode);
          setDiagnosisSnomedDisplay(snomedDisplay);

          let icd10Code = option.value.icd10Code ?? null;
          let icd10Display = option.value.icd10Display ?? null;

          if (snomedCode && !icd10Code) {
            try {
              const mappings = await getTerminologyCie10FromSnomed(snomedCode);
              const firstMapping = mappings[0];
              icd10Code = firstMapping?.cie10code?.trim() || null;
              icd10Display =
                firstMapping?.cie10Description?.trim() ||
                firstMapping?.cie10title?.trim() ||
                null;
              if (icd10Code && !icd10Display) {
                icd10Display = await resolveIcd10Display(icd10Code, icd10Display);
              }
            } catch {
              icd10Code = null;
              icd10Display = null;
            }
            if (requiresMappedCie10 && !icd10Code) {
              const msg = `El diagnostico SNOMED seleccionado (${snomedCode}) no tiene mapeo CIE10.`;
              setDiagnosisSearchMessage(msg);
              onError?.(new Error(msg), msg);
            }
          }

          setDiagnosisIcd10Code(icd10Code);
          setDiagnosisIcd10Display(icd10Display);
          return;
        }

        const icd10Code = option.value.icd10Code ?? option.value.code ?? null;
        const icd10Display = await resolveIcd10Display(
          icd10Code ?? "",
          option.value.icd10Display ?? option.value.description ?? null
        );
        setDiagnosisIcd10Code(icd10Code);
        setDiagnosisIcd10Display(icd10Display);

        let snomedCode: string | null = null;
        let snomedDisplay: string | null = null;

        if (icd10Code) {
          try {
            const mappings = await getTerminologySnomedFromCie10(icd10Code);
            const firstMapping = mappings[0];
            snomedCode = firstMapping?.code?.trim() || null;
            snomedDisplay = firstMapping?.description?.trim() || null;
          } catch {
            snomedCode = null;
            snomedDisplay = null;
          }
        }

        setDiagnosisSnomedCode(snomedCode);
        setDiagnosisSnomedDisplay(snomedDisplay);
      }}
        getItems={loadDiagnosisOptions}
        optionLabel="label"
        className="border2"
        style={{ width: "100%" }}
        placeholder={
          normalizeDiagnosisType(diagnosisType) === "SNOMED"
            ? `Escribir al menos 3 letras para buscar en ${DIAGNOSIS_TYPE_LABEL["SNOMED"]}`
            : `Buscar diagnóstico en ${DIAGNOSIS_TYPE_LABEL["NBU"]}`
        }
      />
    );
  };

  useEffect(() => {
    if (normalizeDiagnosisType(diagnosisType) !== "SNOMED") return;
    if (!diagnosisSnomedCode || diagnosisIcd10Code) return;

    let cancelled = false;
    (async () => {
      try {
        const mappings = await getTerminologyCie10FromSnomed(diagnosisSnomedCode);
        const firstMapping = mappings[0];
        const icd10Code = firstMapping?.cie10code?.trim() || null;
        let icd10Display =
          firstMapping?.cie10Description?.trim() ||
          firstMapping?.cie10title?.trim() ||
          null;
        if (icd10Code && !icd10Display) {
          icd10Display = await resolveIcd10Display(icd10Code, icd10Display);
        }

        if (cancelled) return;
        setDiagnosisIcd10Code(icd10Code);
        setDiagnosisIcd10Display(icd10Display);
      } catch {
        if (cancelled) return;
        if (requiresMappedCie10) {
          const msg = `El diagnostico SNOMED seleccionado (${diagnosisSnomedCode}) no tiene mapeo CIE10.`;
          setDiagnosisSearchMessage(msg);
          onError?.(new Error(msg), msg);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    diagnosisType,
    diagnosisSnomedCode,
    diagnosisIcd10Code,
    requiresMappedCie10,
    resolveIcd10Display,
    onError,
  ]);

  const renderItemsList = () => (
    <div className="ns-items-list">
      <label className="ns-subtitle">Estudios agregados ({items.length})</label>

      {items.length === 0 && <p className="ns-empty-list">Todavia no se han agregado estudios.</p>}

      {items.map((it: any) => {
        const itemId = String(it?.id ?? "");
        const invalidReason = invalidItemReasonById.get(itemId);
        const isInvalid = !!invalidReason;
        return (
          <div
            key={it.id}
            className={`ns-item-row ${isInvalid ? "ns-item-row-invalid" : ""}`}
          >
            <div className="ns-item-content">
              <span className="ns-item-text">
                {it.code ? `${it.code} - ${it.description}` : it.description}
              </span>
              {isInvalid && <small className="ns-item-error">{invalidReason}</small>}
            </div>
            <button type="button" className="ns-remove-button" onClick={() => handleRemoveItem(it.id)}>
              <i className="pi pi-trash" />
            </button>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="prescription-step-study-order">
      <div className="ns-block">
        <label className="ns-label">Paciente</label>
        <div className="ns-patient-name">
          {(selectedPatient as any)?.firstname} {(selectedPatient as any)?.lastname}
          {(selectedPatient as any)?.document && ` - DNI ${(selectedPatient as any).document}`}
        </div>
        <div className="ns-hide-patient">
          <div className="ns-hide-row ns-hide-inline">
            <CustomCheckBox checked={hidePatientData} onChange={(e) => setHidePatientData(e.checked ?? false)} />
            <span className="ns-hide-label">Ocultar datos del paciente</span>
          </div>
        </div>
      </div>

      <div className="ns-row">
        <div className="ns-block">
          <label className="ns-label">Fecha de emision</label>
          <InputDate
            className="input"
            value={emissionDate}
            onChange={(e: any) => {
              const v = e?.value ?? e?.target?.value ?? null;
              setEmissionDate(v instanceof Date ? v : v ? new Date(v) : null);
            }}
            style={{ width: "100%" }}
          />
        </div>

        <div className="ns-block">
          <label className="ns-label">Tipo de diagnóstico</label>
          <Combobox
            value={{
              label: DIAGNOSIS_TYPE_LABEL[normalizeDiagnosisType(diagnosisType)],
              value: normalizeDiagnosisType(diagnosisType),
            }}
            setValue={(option: any) =>
              setDiagnosisType(normalizeDiagnosisType(option?.value as StudyOrderDiagnosisType))
            }
            getItems={async () =>
              (Object.entries(DIAGNOSIS_TYPE_LABEL) as Array<[StudyOrderDiagnosisType, string]>).map(
                ([value, label]) => ({
                  label,
                  value,
                })
              )
            }
            optionLabel="label"
            className="border2"
            style={{ width: "100%" }}
          />
        </div>
      </div>

      <div className="ns-block">
        <label className="ns-label">Diagnostico</label>
        {renderDiagnosisControl()}
        {diagnosisSearchMessage && <small className="ns-helper-text">{diagnosisSearchMessage}</small>}
        {diagnosisMappingMissing && (
          <small className="ns-helper-text">
            Este repositorio requiere diagnostico mapeado a CIE10 para continuar.
          </small>
        )}
      </div>

      <div className="ns-block">
        <label className="ns-label">Descripción de la muestra (Opcional)</label>
        <InputTextCustom
          className="border2"
          value={sampleDescription}
          onChange={(e: any) => setSampleDescription(e.target.value)}
          placeholder="Descripción de la muestra"
          style={{ width: "100%" }}
        />
      </div>

      <div className="ns-block">
        <label className="ns-label">Tipo de estudio *</label>
        <Dropdown
          value={(studyType as any)?.studyType ?? null}
          options={studyTypeDropdownOptions}
          onChange={(e) => {
            const id = e.value;
            const found = studyTypes.find((t: any) => t.studyType === id) ?? null;
            setStudyType(found);
          }}
          optionLabel="label"
          optionValue="value"
          placeholder="Seleccione un tipo de estudio"
          className="border2"
          style={{ width: "100%" }}
          showClear
          filter
        />
      </div>

      <div className="ns-block">
        <label className="ns-label">Estudios a realizar * (Debe seleccionar un tipo de un estudio)</label>
        {isActiviaFlow && (
          <small className="ns-helper-text">
            En Activia solo se permiten practicas con codigo NBU para envio.
          </small>
        )}
        {allowedSourceIds.length > 0 && (
          <small className="ns-helper-text">
            Fuentes permitidas para este repositorio: {allowedSourceIds.join(", ")}.
          </small>
        )}
        {invalidActiviaItems.length > 0 && (
          <small className="ns-helper-text">
            Hay practicas con fuente no permitida en la orden. Revise los items copiados o anteriores para continuar.
          </small>
        )}
        <RadioButtonGroup
          generalClassName="ns-radio-group"
          value={mode === "NOMENCLATOR" ? "FAVOURITE" : mode}
          setValue={(v) => setMode(v as StudyOrderMode)}
          orientation="column"
          enableFullCardClick
          options={[
            { value: "NOMENCLATOR_SOURCE", label: "Buscar por nomenclador" + (sourceLabel ? ` (${sourceLabel})` : "") },
            { value: "FAVOURITE", label: "Seleccionar desde la lista de favoritos" },
            { value: "FREE_TEXT", label: "Ingresar texto libre" },
          ]}
        />
        {renderModeControls()}
        {renderItemsList()}
      </div>
    </div>
  );
}


